/**
 * Redux Slice para gerenciamento de downloads de eBooks
 * Sincroniza com ebookDownloadService
 */

import { createSlice } from '@reduxjs/toolkit';
import { ebookDownloadService } from '../../services/ebookDownloadService';

const initialState = {
  authorizedDownloads: [],
  downloadStats: {
    totalAuthorized: 0,
    totalActive: 0,
    totalExpired: 0,
    totalDownloads: 0,
  },
  downloadHistory: [],
  currentDownload: {
    inProgress: false,
    ebookId: null,
    progress: 0,
    error: null,
  },
  loading: false,
  error: null,
};

const ebookDownloadSlice = createSlice({
  name: 'ebookDownload',
  initialState,

  reducers: {
    // ===== Authorizar Downloads =====
    authorizeDownload: (state, action) => {
      const { ebookId, ebookTitle, paymentInfo } = action.payload;
      const result = ebookDownloadService.authorizeDownload(
        ebookId,
        ebookTitle,
        paymentInfo
      );

      if (result.success) {
        state.authorizedDownloads = ebookDownloadService.getAuthorizedDownloads();
        state.downloadStats = ebookDownloadService.getDownloadStats();
      }

      state.error = result.success ? null : result.message;
    },

    authorizeMultipleDownloads: (state, action) => {
      const { ebooks, paymentInfo } = action.payload;
      const result = ebookDownloadService.authorizeMultipleDownloads(ebooks, paymentInfo);

      state.authorizedDownloads = ebookDownloadService.getAuthorizedDownloads();
      state.downloadStats = ebookDownloadService.getDownloadStats();
      state.error = result.success ? null : result.message;
    },

    // ===== Downloads =====
    startDownload: (state, action) => {
      const { ebookId } = action.payload;
      state.currentDownload.inProgress = true;
      state.currentDownload.ebookId = ebookId;
      state.currentDownload.progress = 0;
      state.currentDownload.error = null;
    },

    updateDownloadProgress: (state, action) => {
      const { progress } = action.payload;
      state.currentDownload.progress = progress;
    },

    completeDownload: (state) => {
      state.currentDownload.inProgress = false;
      state.currentDownload.progress = 100;

      // Atualizar estatísticas
      state.downloadStats = ebookDownloadService.getDownloadStats();
      state.downloadHistory = ebookDownloadService.getDownloadLog();
    },

    failDownload: (state, action) => {
      const { error } = action.payload;
      state.currentDownload.inProgress = false;
      state.currentDownload.error = error;
    },

    // ===== Informações =====
    loadAuthorizedDownloads: (state) => {
      state.authorizedDownloads = ebookDownloadService.getAuthorizedDownloads();
    },

    updateDownloadStats: (state) => {
      state.downloadStats = ebookDownloadService.getDownloadStats();
    },

    loadDownloadHistory: (state) => {
      state.downloadHistory = ebookDownloadService.getDownloadLog();
    },

    // ===== Limpeza =====
    cleanupExpiredDownloads: (state) => {
      ebookDownloadService.cleanupExpiredDownloads();
      state.authorizedDownloads = ebookDownloadService.getAuthorizedDownloads();
      state.downloadStats = ebookDownloadService.getDownloadStats();
    },

    clearAllDownloads: (state) => {
      ebookDownloadService.clearAll();
      state.authorizedDownloads = [];
      state.downloadStats = initialState.downloadStats;
      state.downloadHistory = [];
      state.currentDownload = initialState.currentDownload;
    },

    // ===== Erro =====
    setError: (state, action) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  authorizeDownload,
  authorizeMultipleDownloads,
  startDownload,
  updateDownloadProgress,
  completeDownload,
  failDownload,
  loadAuthorizedDownloads,
  updateDownloadStats,
  loadDownloadHistory,
  cleanupExpiredDownloads,
  clearAllDownloads,
  setError,
  clearError,
} = ebookDownloadSlice.actions;

export default ebookDownloadSlice.reducer;
